import { useMemo } from 'react';
import { Form, Modal, Select, SelectItem, SelectSkeleton, Stack, TextArea, TextInput, Tooltip } from '@carbon/react';
import { Information } from '@carbon/react/icons';
import _, { sortBy } from 'lodash';
import { create, eager, enforce, omitWhen, test } from 'vest';

import useBusinessUnitsQuery from 'shared/api/business-units/useBusinessUnitsQuery';
import useDebouncedValue from 'utils/hooks/useDebouncedValue';
import useForm from 'utils/hooks/useForm';
import useLatest from 'utils/hooks/useLatest';
import useUpdateEffect from 'utils/hooks/useUpdateEffect';
import styles from 'theme/features/inputs/components/employee-group-modal.module.scss';

const formatAttendanceRate = (group) => (group ? Math.round(group.attendanceRate * 100).toString() : '');

const mapDefaultValues = (group) =>
  group
    ? {
        businessUnit: group.businessUnit.id.toString(),
        subGroup: group.subGroup.id.toString(),
        headcount: group.headcount,
        bandA: group.bandA,
        bandAa: group.bandAa,
        attendanceRate: group.attendanceRate == null ? '' : formatAttendanceRate(group),
        notes: group.notes
      }
    : {
        businessUnit: '',
        subGroup: '',
        headcount: '',
        bandA: '',
        bandAa: '',
        attendanceRate: '',
        notes: ''
      };

const isAcceptableAttendanceRateDifference = (defaultRate, newRate, allowedDifference) =>
  newRate <= defaultRate && defaultRate - newRate <= allowedDifference;

const createSuite = (businessUnits) =>
  create((data = {}) => {
    eager();

    test('group', 'Group is required', () => {
      enforce(data.businessUnit).isNotEmpty();
    });

    test('subGroup', 'Sub-group is required', () => {
      enforce(data.subGroup).isNotEmpty();
    });

    test('headcount', 'Planned Headcount is required', () => {
      enforce(data.headcount).isNotEmpty();
    });

    omitWhen(data.attendanceRate === undefined || data.attendanceRate === '', () => {
      const subGroup = businessUnits
        ?.find((x) => x.id === Number(data.businessUnit))
        ?.subGroups?.find((x) => x.id === Number(data.subGroup));

      if (
        typeof subGroup?.attendanceRate === 'number' &&
        isAcceptableAttendanceRateDifference(
          subGroup.attendanceRate * 100,
          Number(data.attendanceRate),
          subGroup.acceptableAttendanceRateDeviation * 100
        )
      ) {
        return;
      }

      test('notes', 'Notes are required due to large attendance rate deviation', () => {
        enforce(data.notes).isNotEmpty();
      });

      test('notes', 'Notes should not exceed 200 characters', () => {
        enforce(data.notes.length).lessThanOrEquals(200);
      });
    });
  });

const EmployeeGroupModal = ({ open = false, editGroup = null, onClose, onSubmit }) => {
  const { data: businessUnits = [], isLoading: isLoadingBusinessUnits } = useBusinessUnitsQuery();
  const businessUnitsRef = useLatest(businessUnits);

  const { register, handleSubmit, watch, reset, setValue } = useForm({
    suite: useMemo(() => createSuite(businessUnits), [businessUnits]),
    defaultValues: mapDefaultValues(editGroup)
  });

  const selectedBusinessUnitId = Number(watch('businessUnit') || undefined);
  const selectedSubGroupId = Number(watch('subGroup') || undefined);
  const customAttendanceRate = watch('attendanceRate');
  const hasCustomAttendanceRate = !!customAttendanceRate;

  const selectedBusinessUnit = useMemo(
    () => businessUnits.find((x) => x.id === selectedBusinessUnitId),
    [businessUnits, selectedBusinessUnitId]
  );
  const subGroups = useMemo(() => selectedBusinessUnit?.subGroups ?? [], [selectedBusinessUnit]);
  const selectedSubGroup = useMemo(
    () => subGroups.find((x) => x.id === selectedSubGroupId),
    [subGroups, selectedSubGroupId]
  );
  const defaultAttendanceRate = formatAttendanceRate(selectedSubGroup);

  const isNotesFieldRequired = useDebouncedValue(
    useMemo(
      () =>
        hasCustomAttendanceRate &&
        selectedSubGroup &&
        !isAcceptableAttendanceRateDifference(
          selectedSubGroup.attendanceRate * 100,
          Number(customAttendanceRate),
          selectedSubGroup.acceptableAttendanceRateDeviation * 100
        ),
      [hasCustomAttendanceRate, selectedSubGroup, customAttendanceRate]
    )
  );

  useUpdateEffect(() => {
    if (!open) {
      return _.noop;
    }

    const defaultValues = mapDefaultValues(editGroup);
    const subscription = watch(({ businessUnit: businessUnitIdValue }, { name, type }) => {
      if (type === 'change' && name === 'businessUnit') {
        const newBusinessUnit = businessUnitsRef.current?.find((b) => b.id === Number(businessUnitIdValue));

        setValue(
          'subGroup',
          newBusinessUnit && newBusinessUnit.subGroups.length === 1 ? newBusinessUnit.subGroups[0].id.toString() : ''
        );
        setValue('attendanceRate', '');
      }
    }, defaultValues);

    reset(defaultValues);

    return () => subscription.unsubscribe();
  }, [open, editGroup]);

  const handleAttendanceRateBlur = () => {
    if (customAttendanceRate === defaultAttendanceRate) {
      setValue('attendanceRate', '');
    }
  };

  const handleFormSubmit = (values) => {
    onSubmit({
      businessUnit: selectedBusinessUnit,
      subGroup: selectedSubGroup,
      attendanceRate: values.attendanceRate ? Number(values.attendanceRate) / 100 : null,
      headcount: Number(values.headcount),
      bandA: Number(values.bandA || '0'),
      bandAa: Number(values.bandAa || '0'),
      notes: values.notes || null
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      modalHeading={editGroup ? 'Edit the group information' : 'Add a group information'}
      primaryButtonText={editGroup ? 'Save' : 'Add to Summary'}
      secondaryButtonText="Reset"
      onRequestClose={onClose}
      preventCloseOnClickOutside
      onRequestSubmit={handleSubmit(handleFormSubmit)}
      onSecondarySubmit={() => reset()}
    >
      <p className={styles.description}>
        Please provide information regarding population groups, subgroups, bands, and attendance rates. If the
        attendance rate is customized, please explain the reason for this decision.
      </p>
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack gap={6}>
          {isLoadingBusinessUnits ? (
            <>
              <SelectSkeleton className={styles.select} />
              <SelectSkeleton className={styles.select} />
            </>
          ) : (
            <>
              <Select
                id="business-unit"
                defaultValue=""
                labelText="Group*"
                className={styles.select}
                {...register('businessUnit')}
              >
                <SelectItem disabled hidden value="" text="Choose a group" />
                {businessUnits.map((x) => (
                  <SelectItem key={x.id} value={x.id} text={x.name} />
                ))}
              </Select>
              <Select
                id="sub-group"
                defaultValue=""
                labelText="Sub-group*"
                className={styles.select}
                disabled={subGroups.length === 0}
                {...register('subGroup')}
              >
                <SelectItem disabled hidden value="" text="Choose a sub-group" />
                {sortBy(subGroups, 'order').map((x) => (
                  <SelectItem key={x.id} value={x.id} text={x.name} />
                ))}
              </Select>
            </>
          )}
          <div className={styles['inputs-row']}>
            <TextInput
              id="headcount"
              labelText="Planned Headcount"
              placeholder="Enter a number"
              type="number"
              min={0}
              {...register('headcount')}
            />
            <TextInput
              id="band-a"
              labelText="Planned Band A"
              placeholder="Enter a number"
              type="number"
              min={0}
              {...register('bandA')}
            />
            <TextInput
              id="band-aa"
              labelText="Planned Band AA"
              placeholder="Enter a number"
              type="number"
              min={0}
              {...register('bandAa')}
            />
          </div>
          <div className={styles['inputs-row']}>
            <TextInput
              id="default-attendance-rate"
              labelText={
                <span className={styles['text-area-tooltip']}>
                  % Attendance Rate
                  <Tooltip
                    align="top-left"
                    label="The amount of time a Business Unit expects their population to be on site. A 40% attendance rate, for example, would indicate that a BU expects their population to be in the office 2 out of 5 days per week."
                  >
                    <button className={styles['text-area-tooltip__trigger']} type="button">
                      <Information />
                    </button>
                  </Tooltip>
                </span>
              }
              type="number"
              readOnly
              value={defaultAttendanceRate}
            />
            <TextInput
              id="attendance-rate"
              labelText="% Custom Attendance Rate"
              placeholder="Enter a number"
              type="number"
              max={100}
              min={0}
              disabled={!selectedSubGroup}
              {...register('attendanceRate', { onBlur: handleAttendanceRateBlur })}
            />
          </div>
          <TextArea
            id="notes"
            className={styles['notes-text-area']}
            labelText={
              <span className={styles['text-area-tooltip']}>
                {`${isNotesFieldRequired ? 'Required ' : ''}Notes for Customized Attendance Rate`}
                <Tooltip
                  align="top-right"
                  label="If the attendance rate is customized, please explain the reason for this decision."
                >
                  <button className={styles['text-area-tooltip__trigger']} type="button">
                    <Information />
                  </button>
                </Tooltip>
              </span>
            }
            placeholder="Add notes here"
            maxCount={200}
            {...register('notes')}
          />
        </Stack>
      </Form>
    </Modal>
  );
};

export default EmployeeGroupModal;
